import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Btn,
  CustomDropdown,
  CustomDropdownWithFilter,
  InputCep,
  InputPhone,
  InputText,
  SectionTitle,
} from '../../../../../components'
import InputCnpjCpf from '../../../../../components/InputCnpjCpf/InputCnpjCpf'
import {
  CTE_PARTE_ORIGENS,
  CTE_SITUACOES_ICMS_PARTE,
  CTE_TIPOS_PESSOA,
} from '../../../../../constants/cte-sem-nota'
import { getParteCte } from '../../../../../services/api/https/deliveries/cte.http'
import { getCnpjDataFocusNfe } from '../../../../../services/api/https/system/system.http'
import { getEmpresas } from '../../../../../services/localstorage/localstorage.service'
import { somenteDigitos } from '../../../../../services/util/documento.service'
import RadioGroup from './RadioGroup'
import SelectClienteTp from './SelectClienteTp'
import SelectOperadorLogistico from './SelectOperadorLogistico'
import {
  ESTILO_BOTAO_PARTE,
  camposFaltantesDoCadastro,
  parteDoCadastro,
  parteVazia,
  tpPadrao,
  tpParaConsulta,
} from './ctePartes.util'

const estiloDica = { color: 'gray', fontSize: 12 }

// Sub-formulário de uma parte do CTe (remetente, destinatário, expedidor, recebedor ou tomador Outros).
// Origem Empresa/Cliente/Operador Logístico pré-preenche a partir do cadastro; Manual autopreenche por CNPJ
// (Focus). Todos os campos ficam editáveis e o que for enviado vira snapshot do CTe (não altera o cadastro).
function CteParteForm({
  papel,
  titulo,
  parte,
  onChange,
  ufs,
  selectedTp,
  carregarCidades,
  onError,
  somentePj = false,
  telefoneObrigatorio = false,
  origens = CTE_PARTE_ORIGENS,
  onRemover = null,
  dica = null,
}) {
  const [cidades, setCidades] = useState([])
  const [carregando, setCarregando] = useState(false)
  const [avisoCadastro, setAvisoCadastro] = useState(null)
  const parteRef = useRef(parte)
  parteRef.current = parte

  const empresas = useMemo(() => getEmpresas(), [])
  // A empresa das notas pode não estar entre as empresas do usuário: entra como opção para aparecer no campo
  const opcoesEmpresas = useMemo(() => {
    if (!parte.company_id || empresas.some(empresa => empresa.id === parte.company_id))
      return empresas
    return [...empresas, { id: parte.company_id, name: parte.nome }]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresas, parte.company_id])
  const empresaSelecionada = opcoesEmpresas.find(empresa => empresa.id === parte.company_id) || null
  const clienteSelecionado = parte.client_id
    ? { id: parte.client_id, rotulo: `${parte.nome} - ${parte.cnpj_cpf}` }
    : null
  const ufId = parte.uf ? parte.uf.id : null

  // Cidades da UF selecionada; se a cidade pré-preenchida não existir no cadastro, exige seleção manual
  useEffect(() => {
    let ativo = true
    if (!ufId) {
      setCidades([])
      return undefined
    }
    carregarCidades(ufId)
      .then(lista => {
        if (!ativo) return
        setCidades(lista)
        const atual = parteRef.current.city
        if (atual && !lista.some(cidade => cidade.id === atual.id)) {
          onChange({ ...parteRef.current, city: null })
          onError(
            new Error(
              `${titulo}: cidade não encontrada no cadastro de cidades, selecione manualmente`,
            ),
          )
        }
      })
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufId])

  // Helpers
  const atualizar = patch => onChange({ ...parteRef.current, ...patch })

  const carregarCadastro = async (tipo, id) => {
    try {
      setCarregando(true)
      setAvisoCadastro(null)
      const dados = await getParteCte(tpParaConsulta(selectedTp), tipo, id)
      const carregada = parteDoCadastro(dados, ufs)
      onChange(carregada)
      if (tipo === 'tp') {
        const faltantes = camposFaltantesDoCadastro(carregada)
        if (faltantes.length) {
          setAvisoCadastro(
            `Cadastro do Operador Logístico sem ${faltantes.join(', ')}: complete abaixo (vale só para este CTe) ou entre em contato com o suporte para que essas informações sejam cadastradas permanentemente.`,
          )
        }
      }
    } catch (error) {
      onError(error)
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => {
    if (parte.origem !== 'tp' || parte.tp_id) return
    const padrao = tpPadrao(selectedTp)
    if (padrao) carregarCadastro('tp', padrao)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parte.origem])

  const buscarCnpj = async cnpj => {
    try {
      setCarregando(true)
      const dados = await getCnpjDataFocusNfe(cnpj)
      const endereco = dados.endereco || {}
      const cidadeId = parseInt(endereco.codigo_ibge, 10)
      atualizar({
        nome: String(dados.razao_social || '').slice(0, 60),
        logradouro: endereco.logradouro || '',
        numero: endereco.numero || '',
        bairro: endereco.bairro || '',
        cep: somenteDigitos(endereco.cep),
        uf: ufs.find(item => item.uf === endereco.uf) || null,
        city: cidadeId ? { id: cidadeId, name: endereco.nome_municipio || '' } : null,
      })
    } catch (error) {
      onError(error)
    } finally {
      setCarregando(false)
    }
  }

  // Events
  const onOrigemChange = origem => {
    setAvisoCadastro(null)
    onChange(parteVazia(origem))
  }

  const onTipoPessoaChange = tipoPessoa => {
    atualizar({
      tipo_pessoa: tipoPessoa,
      cnpj_cpf: '',
      ...(tipoPessoa === 'PF' ? { situacao_icms: 'nao_contribuinte', inscricao_estadual: '' } : {}),
    })
  }

  const onDocumentoChange = ({ value }) => {
    atualizar({ cnpj_cpf: value })
    if (parte.origem === 'manual' && parte.tipo_pessoa === 'PJ' && value.length === 14) {
      buscarCnpj(value)
    }
  }

  const situacaoSelecionada =
    CTE_SITUACOES_ICMS_PARTE.find(item => item.value === parte.situacao_icms) || null

  // Render
  return (
    <>
      <div className="p-col-12">
        {onRemover ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <SectionTitle title={titulo} />
            </div>
            <Btn
              onClick={onRemover}
              backgroundColor="#e5484d"
              style={{ ...ESTILO_BOTAO_PARTE, marginLeft: 10 }}
            >
              Remover
            </Btn>
          </div>
        ) : (
          <SectionTitle title={titulo} />
        )}
        {dica && <span style={estiloDica}>{dica}</span>}
        {origens.length > 1 && (
          <div style={{ marginTop: 5 }}>
            <RadioGroup
              nome={`${papel}-origem`}
              items={origens}
              value={parte.origem}
              onChange={onOrigemChange}
            />
          </div>
        )}
      </div>

      {parte.origem === 'empresa' && (
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="name"
            options={opcoesEmpresas}
            value={empresaSelecionada}
            label="Empresa:*"
            filter={true}
            disabled={carregando}
            onChange={empresa => carregarCadastro('empresa', empresa.id)}
          />
        </div>
      )}

      {parte.origem === 'cliente' && (
        <div className="p-md-6 p-col-12">
          <SelectClienteTp
            selectedTp={tpParaConsulta(selectedTp)}
            selecionado={clienteSelecionado}
            disabled={carregando}
            onSelect={cliente => carregarCadastro('cliente', cliente.id)}
            onError={onError}
          />
        </div>
      )}

      {parte.origem === 'tp' && (
        <div className="p-md-6 p-col-12">
          <SelectOperadorLogistico
            value={parte.tp_id}
            disabled={carregando}
            onChange={tp => carregarCadastro('tp', tp.id)}
          />
          {avisoCadastro && <span style={{ color: '#e67e22', fontSize: 12 }}>{avisoCadastro}</span>}
        </div>
      )}

      {!somentePj && (
        <div className="p-md-4 p-col-12" style={{ marginTop: 10 }}>
          <label
            style={{ fontSize: '0.8rem', color: '#14B0BF', display: 'block', marginBottom: 6 }}
          >
            Tipo de pessoa:*
          </label>
          <RadioGroup
            nome={`${papel}-tipo-pessoa`}
            items={CTE_TIPOS_PESSOA}
            value={parte.tipo_pessoa}
            onChange={onTipoPessoaChange}
          />
        </div>
      )}
      <div className="p-md-3 p-col-12">
        <InputCnpjCpf
          label={parte.tipo_pessoa === 'PF' ? 'CPF:*' : 'CNPJ:*'}
          marginTop={10}
          value={parte.cnpj_cpf}
          onChange={onDocumentoChange}
        />
        {parte.origem === 'manual' && parte.tipo_pessoa === 'PJ' && (
          <span style={estiloDica}>
            Ao completar o CNPJ, nome e endereço são buscados na Receita (IE continua manual)
          </span>
        )}
      </div>
      <div className="p-md-5 p-col-12">
        <InputText
          label="Razão social / Nome:*"
          marginTop={10}
          value={parte.nome}
          maxLength={60}
          onChange={nome => atualizar({ nome })}
        />
      </div>

      <div className="p-md-5 p-col-12">
        <CustomDropdown
          optionLabel="label"
          options={CTE_SITUACOES_ICMS_PARTE}
          value={situacaoSelecionada}
          label="Situação no ICMS:*"
          onChange={item =>
            atualizar({
              situacao_icms: item.value,
              inscricao_estadual: item.value === 'contribuinte' ? parte.inscricao_estadual : '',
            })
          }
        />
      </div>
      <div className="p-md-3 p-col-12">
        {parte.situacao_icms === 'contribuinte' && (
          <InputText
            label="Inscrição Estadual:*"
            marginTop={10}
            value={parte.inscricao_estadual}
            maxLength={14}
            onChange={ie => atualizar({ inscricao_estadual: somenteDigitos(ie) })}
          />
        )}
        {parte.situacao_icms === 'isento' && (
          <InputText
            label="Inscrição Estadual:"
            marginTop={10}
            value="ISENTO"
            disabled={true}
            onChange={() => {}}
          />
        )}
      </div>
      <div className="p-md-4 p-col-12">
        <InputPhone
          label={telefoneObrigatorio ? 'Telefone:*' : 'Telefone:'}
          marginTop={10}
          value={parte.telefone}
          onChange={telefone => atualizar({ telefone: telefone || '' })}
        />
      </div>

      <div className="p-md-6 p-col-12">
        <InputText
          label="Logradouro:*"
          marginTop={10}
          value={parte.logradouro}
          maxLength={255}
          onChange={logradouro => atualizar({ logradouro })}
        />
      </div>
      <div className="p-md-2 p-col-12">
        <InputText
          label="Número:*"
          marginTop={10}
          value={parte.numero}
          maxLength={60}
          onChange={numero => atualizar({ numero })}
        />
      </div>
      <div className="p-md-4 p-col-12">
        <InputText
          label="Bairro:*"
          marginTop={10}
          value={parte.bairro}
          maxLength={60}
          onChange={bairro => atualizar({ bairro })}
        />
      </div>
      <div className="p-md-3 p-col-12">
        <CustomDropdown
          optionLabel="uf"
          options={ufs}
          value={parte.uf}
          label="UF:*"
          filter={true}
          onChange={uf => atualizar({ uf, city: null })}
        />
      </div>
      <div className="p-md-6 p-col-12">
        <CustomDropdownWithFilter
          optionLabel="name"
          options={cidades}
          value={parte.city}
          label="Cidade:*"
          filter={true}
          disabled={!parte.uf}
          onChange={city => atualizar({ city })}
        />
      </div>
      <div className="p-md-3 p-col-12">
        <InputCep
          label="CEP:*"
          marginTop={10}
          value={parte.cep}
          onChange={values => atualizar({ cep: values.value })}
        />
      </div>
      <div className="p-col-12" style={{ paddingTop: 0 }}>
        <span style={estiloDica}>
          Alterações feitas aqui valem só para este CTe e não atualizam o cadastro.
        </span>
      </div>
    </>
  )
}

export default React.memo(CteParteForm)
